<template>
  <page>
    <div class="pane-container">
      <SearchRenderer :query="searchForm" :schema="searchSchema" @search="handleSearch" @reset="handleReset">
        <template #search-vipLimit="{ query }">
          <el-select
            v-model="query.vip_limit"
            multiple
            placeholder="全部"
            clearable
            style="width: 100%"
            @change="handleVipLevelChange"
          >
            <el-option label="全部" value="all" />
            <el-option v-for="item in vipOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </template>
      </SearchRenderer>
    </div>

    <div class="pane-container" style="margin-top: 12px">
      <div class="actions">
        <el-button type="primary" size="mini" @click="handleAddType">新增客服类型</el-button>
        <el-button type="primary" size="mini" @click="handleAddService">新增客服</el-button>
        <el-button type="primary" size="mini" @click="handleAdd724Service">7*24小时客服</el-button>
      </div>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-table
            :data="typeList"
            border
            stripe
            size="small"
            highlight-current-row
            style="width: 100%"
            @current-change="handleTypeCurrentChange"
          >
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="type_name" label="客服类型" min-width="120" />
            <el-table-column label="操作" width="100" fixed="right" align="center">
              <template #default="{ row }">
                <el-button type="text" size="small" @click="handleEditType(row)">编辑</el-button>
                <el-button type="text" size="small" class="danger-button" @click="handleDeleteType(row)"
                  >删除</el-button
                >
              </template>
            </el-table-column>
          </el-table>
        </el-col>
        <el-col :span="16">
          <el-table
            v-loading="serviceLoading"
            :data="serviceList"
            border
            stripe
            size="small"
            highlight-current-row
            style="width: 100%"
          >
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="name" label="客服名称" min-width="100" show-overflow-tooltip />
            <el-table-column prop="account" label="客服账号" width="100" show-overflow-tooltip />
            <el-table-column prop="config_url" label="配置地址" min-width="120" show-overflow-tooltip />
            <el-table-column prop="icon_url" label="图标" width="60" align="center">
              <template #default="{ row }">
                <img v-if="row.icon_url" :src="row.icon_url" class="service-icon-img" alt="" />
                <span v-else>--</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80" align="center">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
                  {{ row.status === 1 ? '启用' : '停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="客服类型" width="100" show-overflow-tooltip>
              <template #default="{ row }">
                {{ typeOptions.find(o => o.value === row.type_id)?.label || '--' }}
              </template>
            </el-table-column>
            <el-table-column label="适用VIP等级" min-width="140" show-overflow-tooltip>
              <template #default="{ row }">
                {{ formatVipLevels(row.vip_limit) }}
              </template>
            </el-table-column>
            <el-table-column prop="sort" label="排序" width="60" align="center" />
            <el-table-column label="操作" width="100" fixed="right" align="center">
              <template #default="{ row }">
                <el-button type="text" size="small" @click="handleEditService(row)">编辑</el-button>
                <el-button type="text" size="small" class="danger-button" @click="handleDeleteService(row)"
                  >删除</el-button
                >
              </template>
            </el-table-column>
          </el-table>
          <div style="text-align: right; margin-top: 15px">
            <el-pagination
              :current-page="servicePage"
              :page-sizes="[10, 20, 30, 50]"
              :page-size="servicePageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="serviceTotal"
              @size-change="handleServiceSizeChange"
              @current-change="handleServicePageChange"
            />
          </div>
        </el-col>
      </el-row>
    </div>

    <CustomerServiceTypeDialog
      :visible.sync="typeDialogVisible"
      :dialog-title="typeDialogTitle"
      :form-data="currentTypeForm"
      @success="handleTypeDialogSuccess"
    />
    <CustomerServiceDialog
      :visible.sync="serviceDialogVisible"
      :dialog-title="serviceDialogTitle"
      :form-data="currentServiceForm"
      :type-options="typeOptions"
      :is-on-line-service="isOnLineService"
      @success="handleServiceDialogSuccess"
    />
  </page>
</template>

<script setup>
import {
  deleteCustomerService,
  deleteCustomerServiceType,
  getCustomerService24,
  getCustomerServiceList,
  getCustomerServiceTypeList,
} from '@/api/content/customerService'
import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue'
import SearchRenderer from '@/components/SchemaTablePage/SearchRenderer.vue'
import CustomerServiceDialog from './components/CustomerServiceDialog.vue'
import CustomerServiceTypeDialog from './components/CustomerServiceTypeDialog.vue'

const VIP_OPTIONS = [
  { label: 'VIP0', value: 0 },
  { label: 'VIP1', value: 1 },
  { label: 'VIP2', value: 2 },
  { label: 'VIP3', value: 3 },
  { label: 'VIP4', value: 4 },
  { label: 'VIP5', value: 5 },
  { label: 'VIP6', value: 6 },
  { label: 'VIP7', value: 7 },
  { label: 'VIP8', value: 8 },
  { label: 'VIP9', value: 9 },
]

const { proxy } = getCurrentInstance()

const searchForm = reactive({
  name: '',
  type_id: '',
  status: '',
  vip_limit: [],
})

const STATUS_OPTIONS = [
  { key: 'all', label: '全部', value: '' },
  { key: 'on', label: '启用', value: 1 },
  { key: 'off', label: '停用', value: 2 },
]

const vipOptions = VIP_OPTIONS

const typeList = ref([])
const typeDialogVisible = ref(false)
const typeDialogTitle = ref('新增客服类型')
const currentTypeForm = ref({})

const serviceList = ref([])
const serviceLoading = ref(false)
const servicePage = ref(1)
const servicePageSize = ref(10)
const serviceTotal = ref(0)
const selectedTypeId = ref(null)
const serviceDialogVisible = ref(false)
const serviceDialogTitle = ref('新增客服')
const currentServiceForm = ref({})

const typeOptions = computed(() => typeList.value.map(item => ({ label: item.type_name, value: item.id })))

const searchSchema = computed(() => [
  {
    key: 'name',
    prop: 'name',
    label: '客服名称',
    component: 'input',
    componentProps: { placeholder: '请输入客服名称', clearable: true },
  },
  {
    key: 'type_id',
    prop: 'type_id',
    label: '客服类型',
    component: 'select',
    componentProps: { placeholder: '全部', clearable: true },
    options: [
      { key: 'type_all', label: '全部', value: '' },
      ...typeOptions.value.map(o => ({ key: `type_${o.value}`, label: o.label, value: o.value })),
    ],
  },
  {
    key: 'status',
    prop: 'status',
    label: '状态',
    component: 'select',
    componentProps: { placeholder: '全部', clearable: true },
    options: STATUS_OPTIONS,
  },
  {
    key: 'vip_limit',
    prop: 'vip_limit',
    label: '适用VIP等级',
    component: 'slot',
    slot: 'search-vipLimit',
  },
  { key: 'actions', component: 'actions' },
])

function handleVipLevelChange(value) {
  if (value.includes('all')) {
    if (searchForm.vip_limit.length - 1 === vipOptions.length) {
      searchForm.vip_limit = []
    } else {
      searchForm.vip_limit = vipOptions.map(item => item.value)
    }
  } else {
    searchForm.vip_limit = value
  }
}
function formatVipLevels(vipLevels) {
  if (!vipLevels || !Array.isArray(vipLevels) || vipLevels.length === 0) return '--'
  return vipLevels.map(v => VIP_OPTIONS.find(o => o.value === v)?.label || `VIP${v}`).join(', ')
}

function fetchTypeList() {
  getCustomerServiceTypeList()
    .then(res => {
      const data = res.data
      if (Array.isArray(data)) {
        typeList.value = data
      } else if (data && Array.isArray(data.list)) {
        typeList.value = data.list
      } else {
        typeList.value = []
      }
    })
    .catch(error => {
      console.error('获取客服类型失败', error)
      proxy.$message.error(error?.response?.data?.message || '获取客服类型失败')
    })
}

function getVipLimit() {
  if (searchForm.vip_limit[0] === 'all') {
    return vipOptions.map(item => item.value)
  } else if (searchForm.vip_limit.length > 0) {
    return searchForm.vip_limit
  } else {
    return []
  }
}
function fetchServiceList() {
  serviceLoading.value = true
  const vip_limit = getVipLimit()
  const params = {
    page: servicePage.value,
    page_size: servicePageSize.value,
    name: searchForm.name || undefined,
    type_id: selectedTypeId.value || searchForm.type_id || undefined,
    status: searchForm.status !== '' ? searchForm.status : undefined,
    vip_limit: vip_limit,
  }
  getCustomerServiceList(params)
    .then(res => {
      const data = res.data || {}
      serviceList.value = Array.isArray(data.list) ? data.list : []
      serviceTotal.value = typeof data.total === 'number' ? data.total : 0
    })
    .catch(error => {
      console.error('获取客服列表失败', error)
      proxy.$message.error(error?.response?.data?.message || '获取客服列表失败')
    })
    .finally(() => {
      serviceLoading.value = false
    })
}

function handleSearch() {
  servicePage.value = 1
  fetchServiceList()
}

function handleReset() {
  searchForm.name = ''
  searchForm.type_id = ''
  searchForm.status = ''
  searchForm.vip_limit = []
  servicePage.value = 1
  fetchServiceList()
}

function handleTypeCurrentChange(row) {
  selectedTypeId.value = row ? row.id : null
  servicePage.value = 1
  fetchServiceList()
}

function handleAddType() {
  typeDialogTitle.value = '新增客服类型'
  currentTypeForm.value = {}
  typeDialogVisible.value = true
}

function handleEditType(row) {
  typeDialogTitle.value = '编辑客服类型'
  currentTypeForm.value = { ...row }
  typeDialogVisible.value = true
}

function handleDeleteType(row) {
  proxy
    .$confirm('确认删除该客服类型吗？', '提示', { type: 'warning' })
    .then(() => deleteCustomerServiceType(row.id))
    .then(() => {
      proxy.$message.success('删除成功')
      fetchTypeList()
      if (selectedTypeId.value === row.id) {
        selectedTypeId.value = null
        fetchServiceList()
      }
    })
    .catch(() => {})
}

function handleTypeDialogSuccess() {
  fetchTypeList()
}

function handleAddService() {
  isOnLineService.value = false
  serviceDialogTitle.value = '新增客服'
  currentServiceForm.value = {}
  serviceDialogVisible.value = true
}

const isOnLineService = ref(false)
function handleAdd724Service() {
  isOnLineService.value = true
  serviceDialogTitle.value = '配置7*24小时客服'
  getCustomerService24()
    .then(res => {
      const data = res.data || {}
      currentServiceForm.value = { config_url: data.config_url ?? '' }
      serviceDialogVisible.value = true
    })
    .catch(error => {
      console.error('获取7*24小时客服配置失败', error)
      proxy.$message.error(error?.response?.data?.message || '获取配置失败')
      currentServiceForm.value = { config_url: '' }
      serviceDialogVisible.value = true
    })
}
function handleEditService(row) {
  isOnLineService.value = false
  serviceDialogTitle.value = '编辑客服'
  currentServiceForm.value = { ...row }
  serviceDialogVisible.value = true
}

function handleDeleteService(row) {
  proxy
    .$confirm('确认删除该客服吗？', '提示', { type: 'warning' })
    .then(() => deleteCustomerService(row.id))
    .then(() => {
      proxy.$message.success('删除成功')
      fetchServiceList()
    })
    .catch(() => {})
}

function handleServiceDialogSuccess() {
  fetchServiceList()
}

function handleServiceSizeChange(val) {
  servicePageSize.value = val
  servicePage.value = 1
  fetchServiceList()
}

function handleServicePageChange(val) {
  servicePage.value = val
  fetchServiceList()
}

onMounted(() => {
  fetchTypeList()
  fetchServiceList()
})
</script>

<style lang="scss" scoped>
.service-icon-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.pane-container {
  background: #fff;
  padding: 12px;
  .actions {
    margin-bottom: 12px;
  }
}
</style>
